import api from "./api";

// Currencies the app supports. Job amounts are stored as entered, with a
// `currencyType` recording which currency the value is in; conversion is a
// display-only concern (job Detail page, Process Optimization results).
export const CURRENCY_TYPES = ["USD", "PKR", "EUR", "GBP"];

const CURRENCY_SYMBOLS = {
  USD: "$",
  PKR: "₨",
  EUR: "€",
  GBP: "£",
};

// Fallback rates (relative to USD, i.e. 1 USD = rate units of the currency).
// Used only when the API is unreachable so conversions never produce NaN.
const DEFAULT_RATES = {
  USD: 1,
  PKR: 278,
  EUR: 0.92,
  GBP: 0.79,
};

// Turn the API payload ([{ currency, rate }]) into a { CODE: rate } map,
// merged over defaults so every supported currency always has a rate.
function toRatesMap(payload) {
  const map = { ...DEFAULT_RATES };
  const arr = Array.isArray(payload) ? payload : payload?.data;
  if (Array.isArray(arr)) {
    arr.forEach((item) => {
      const code = String(item?.currency || "").toUpperCase();
      const rate = Number(item?.rate);
      if (code && Number.isFinite(rate) && rate > 0) {
        map[code] = rate;
      }
    });
  }
  return map;
}

export function getDefaultRatesMap() {
  return { ...DEFAULT_RATES };
}

// In-memory cache + in-flight coalescing so repeated mounts (incl. React
// StrictMode's double-invoke) and concurrent callers share a single request.
const RATES_CACHE_TTL_MS = 1000 * 60 * 5; // 5 minutes
let _ratesCache = null; // { map, timestamp }
let _ratesInFlight = null;

// Cached conversion rates.
export async function getCurrencyRates() {
  if (_ratesCache && Date.now() - _ratesCache.timestamp < RATES_CACHE_TTL_MS) {
    return { ..._ratesCache.map };
  }
  if (_ratesInFlight) return _ratesInFlight;

  _ratesInFlight = (async () => {
    try {
      const res = await api.get("/currency-rate");
      const map = toRatesMap(res);
      _ratesCache = { map, timestamp: Date.now() };
      return { ...map };
    } finally {
      _ratesInFlight = null;
    }
  })();

  return _ratesInFlight;
}

// Force a refresh of the latest rates from the provider (bypasses the cache).
export async function refreshCurrencyRates() {
  const res = await api.get("/currency-rate/rates");
  const map = toRatesMap(res);
  _ratesCache = { map, timestamp: Date.now() };
  return { ...map };
}

// Convert an amount from one currency to another. Rates are relative to USD
// (1 USD = rate units of the currency), so we hop through USD.
export function convertCurrency(amount, fromCurrency, toCurrency, rates) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  const fromRate = Number(rates?.[fromCurrency]) || 1;
  const toRate = Number(rates?.[toCurrency]) || 1;
  return (value / fromRate) * toRate;
}

// Format an amount with its currency symbol and thousands separators,
// e.g. formatMoney(1234.5, "PKR") -> "₨1,234.50".
export function formatMoney(amount, currency = "USD") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "-";
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency] || currency;
}
